import React, { useEffect, useState } from "react";
import { maintenanceApi, OrgItem, StationTypeItem, GstStateItem } from "../../../api/maintenance";
import { X } from "lucide-react";
import { useNotice } from "../shared/useNotice";

interface StationFormProps {
  onClose: () => void;
  onCreated: () => void;
}

const StationForm: React.FC<StationFormProps> = ({ onClose, onCreated }) => {
  const [orgs, setOrgs] = useState<OrgItem[]>([]);
  const [stationTypes, setStationTypes] = useState<StationTypeItem[]>([]);
  const [gstStates, setGstStates] = useState<GstStateItem[]>([]);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [orgId, setOrgId] = useState<number | "">("");
  const [locationLat, setLocationLat] = useState("");
  const [locationLon, setLocationLon] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [stationTypeId, setStationTypeId] = useState<number | "">("");
  const [gstStateCode, setGstStateCode] = useState<number | "">("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useNotice("error", "");

  useEffect(() => {
    Promise.all([
      maintenanceApi.getOrgs(),
      maintenanceApi.getStationTypes(),
      maintenanceApi.getGstStates(),
    ]).then(([orgsRes, typesRes, gstRes]) => {
      setOrgs(orgsRes.data);
      setStationTypes(typesRes.data);
      setGstStates(gstRes.data);
    }).catch(console.error);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !orgId || !locationLat || !locationLon || !gstStateCode) {
      setError("Name, Organization, Latitude, Longitude, and GST State are required");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await maintenanceApi.createStation({
        name: name.trim(),
        org_id: orgId as number,
        location_lat: parseFloat(locationLat),
        location_lon: parseFloat(locationLon),
        text_address: address.trim() || undefined,
        is_active: isActive,
        charging_station_type_id: stationTypeId ? (stationTypeId as number) : undefined,
        gst_state_code: gstStateCode as number,
      });
      onCreated();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to create station");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="maint-form-overlay">
      <div className="maint-form-card">
        <div className="maint-form-header">
          <h3>Add Station</h3>
          <button onClick={onClose} className="maint-btn-icon"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <label>
            Name *
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label>
            Organization *
            <select value={orgId} onChange={(e) => setOrgId(Number(e.target.value))} required>
              <option value="">Select organization...</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </label>
          <label>
            Address
            <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} />
          </label>
          <div className="maint-form-row">
            <label>
              Latitude *
              <input type="number" step="any" value={locationLat} onChange={(e) => setLocationLat(e.target.value)} required />
            </label>
            <label>
              Longitude *
              <input type="number" step="any" value={locationLon} onChange={(e) => setLocationLon(e.target.value)} required />
            </label>
          </div>
          <label>
            GST State *
            <select value={gstStateCode} onChange={(e) => setGstStateCode(e.target.value ? Number(e.target.value) : "")} required>
              <option value="">Select GST state...</option>
              {gstStates.map((g) => (
                <option key={g.state_code} value={g.state_code}>{g.state_name} ({g.state_short})</option>
              ))}
            </select>
          </label>
          <div className="maint-form-row">
            <label>
              Station Type
              <select value={stationTypeId} onChange={(e) => setStationTypeId(e.target.value ? Number(e.target.value) : "")}>
                <option value="">None</option>
                {stationTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </label>
            <label>
              Active
              <select value={isActive ? "true" : "false"} onChange={(e) => setIsActive(e.target.value === "true")}>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </label>
          </div>
          {error && <p className="maint-form-error">{error}</p>}
          <div className="maint-form-actions">
            <button type="button" onClick={onClose} className="maint-btn-secondary">Cancel</button>
            <button type="submit" disabled={submitting} className="maint-btn-primary">
              {submitting ? "Creating..." : "Create Station"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StationForm;
